import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { FiRefreshCcw, FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";
import { MdOutlineSettings } from "react-icons/md";
import Axios from "../../_services/caller.service";
import styles from "./HsSettings.module.scss";
import Loader from "../UiComponents/loader/Loader";
import Button from "../UiComponents/Button";
import Modal from "../UiComponents/modals/Modal";
import useModal from "../../hooks/useModal";

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatDate = (val) => {
  if (!val) return "Aucune";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "Aucune";
  return d.toLocaleDateString("fr-FR", { year: "numeric", month: "2-digit", day: "2-digit" });
};

const toInputDate = (val) => {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const EMPTY_FORM = { version: "", channel: "STANDARD", mandatory_date: "" };

// ─── Toggle row ─────────────────────────────────────────────────────────────

const ToggleRow = ({ label, description, checked, disabled, onChange }) => (
  <div className={styles.toggleRow}>
    <div className={styles.toggleInfo}>
      <span className={styles.toggleLabel}>{label}</span>
      <span className={styles.toggleDescription}>{description}</span>
    </div>
    <label className={`${styles.toggle} ${disabled ? styles.toggleDisabled : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <span className={styles.toggleSlider} />
    </label>
  </div>
);

// ─── Main component ──────────────────────────────────────────────────────────

const HsSettings = () => {
  // ── Global settings ──────────────────────────────────────────
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);

  // ── Versions ─────────────────────────────────────────────────
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(true);
  const [versionsRefreshing, setVersionsRefreshing] = useState(false);

  // ── Modal (add / edit) ───────────────────────────────────────
  const { isShowing: isFormShowing, toggle: toggleForm } = useModal();
  const { isShowing: isDeleteShowing, toggle: toggleDelete } = useModal();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [formSaving, setFormSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ── Modal confirmation toggle ────────────────────────────────
  const { isShowing: isToggleConfirmShowing, toggle: toggleConfirmModal } = useModal();
  const [pendingToggle, setPendingToggle] = useState(null); // { field, nextValue }

  // ── Fetch settings ───────────────────────────────────────────
  const fetchSettings = useCallback(() => {
    setSettingsLoading(true);
    Axios.get("/hsSettings/settings")
      .then((res) => setSettings(res.data.data))
      .catch(() => toast.error("Impossible de charger les réglages"))
      .finally(() => setSettingsLoading(false));
  }, []);

  // ── Fetch versions ───────────────────────────────────────────
  const fetchVersions = useCallback((soft = false) => {
    if (soft) setVersionsRefreshing(true);
    else setVersionsLoading(true);

    Axios.get("/hsSettings/versions")
      .then((res) => setVersions(res.data.data || []))
      .catch(() => toast.error("Impossible de charger les versions"))
      .finally(() => {
        setVersionsLoading(false);
        setVersionsRefreshing(false);
      });
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchVersions();
  }, [fetchSettings, fetchVersions]);

  // ── Toggle change — ouvre la confirmation ───────────────────
  const handleToggle = (field) => {
    if (!settings || settingsSaving) return;
    setPendingToggle({ field, nextValue: !settings[field] });
    toggleConfirmModal();
  };

  // ── Confirmation du toggle ───────────────────────────────────
  const confirmToggle = () => {
    if (!pendingToggle) return;
    const { field, nextValue } = pendingToggle;
    const next = { ...settings, [field]: nextValue };
    toggleConfirmModal();
    setPendingToggle(null);
    setSettingsSaving(true);
    Axios.put("/hsSettings/settings", {
      update_disabled: next.update_disabled,
      calibration_disabled: next.calibration_disabled,
    })
      .then(() => {
        setSettings(next);
        toast.success("Réglage mis à jour");
      })
      .catch(() => toast.error("Erreur lors de la sauvegarde"))
      .finally(() => setSettingsSaving(false));
  };

  // ── Open add form ────────────────────────────────────────────
  const openAdd = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    toggleForm();
  };

  // ── Open edit form ───────────────────────────────────────────
  const openEdit = (v) => {
    setEditingId(v.id);
    setFormData({
      version: v.version,
      channel: v.channel,
      mandatory_date: toInputDate(v.mandatory_date),
    });
    toggleForm();
  };

  // ── Open delete confirmation ─────────────────────────────────
  const openDelete = (v) => {
    setDeleteTarget(v);
    toggleDelete();
  };

  // ── Submit form ──────────────────────────────────────────────
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.version.trim()) return toast.error("Version obligatoire");
    if (formData.version.trim().length > 32) return toast.error("Version trop longue (max 32 car.)");

    setFormSaving(true);
    const payload = {
      version: formData.version.trim(),
      channel: formData.channel,
      mandatory_date: formData.mandatory_date || null,
    };

    const request = editingId
      ? Axios.put(`/hsSettings/versions/${editingId}`, payload)
      : Axios.post("/hsSettings/versions", payload);

    request
      .then(() => {
        toast.success(editingId ? "Version mise à jour" : "Version créée");
        toggleForm();
        fetchVersions(true);
      })
      .catch((err) => {
        const msg = err.response?.data?.message || "Erreur lors de la sauvegarde";
        toast.error(msg);
      })
      .finally(() => setFormSaving(false));
  };

  // ── Confirm delete ───────────────────────────────────────────
  const handleDelete = () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    Axios.delete(`/hsSettings/versions/${deleteTarget.id}`)
      .then(() => {
        toast.success("Version supprimée");
        toggleDelete();
        fetchVersions(true);
      })
      .catch((err) => {
        const msg = err.response?.data?.message || "Erreur lors de la suppression";
        toast.error(msg);
      })
      .finally(() => setDeleteLoading(false));
  };

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className={styles.header}>
        <h2>
          <MdOutlineSettings className={styles.headerIcon} />
          HS Settings
        </h2>
      </div>

      {/* ══════════════════════════════════════════════════════════════
          SECTION 1 — Réglages globaux
      ══════════════════════════════════════════════════════════════ */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Réglages globaux Hearing Space</h3>

        <div className={styles.moduleCard}>
          {settingsLoading ? (
            <div className={styles.loaderWrapper}><Loader /></div>
          ) : settings ? (
            <>
              <ToggleRow
                label="Désactiver les mises à jour"
                description="Désactive globalement les mises à jour de toutes les installations Hearing Space."
                checked={settings.update_disabled}
                disabled={settingsSaving}
                onChange={() => handleToggle("update_disabled")}
              />
              <div className={styles.divider} />
              <ToggleRow
                label="Désactiver les calibrations"
                description="Désactive globalement les calibrations de toutes les installations Hearing Space."
                checked={settings.calibration_disabled}
                disabled={settingsSaving}
                onChange={() => handleToggle("calibration_disabled")}
              />
              {settingsSaving && (
                <div className={styles.savingBadge}>Sauvegarde…</div>
              )}
            </>
          ) : (
            <p className={styles.errorText}>Impossible de charger les réglages.</p>
          )}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          SECTION 2 — Gestion des versions
      ══════════════════════════════════════════════════════════════ */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Gestion des versions Hearing Space</h3>
          <div className={styles.sectionActions}>
            <button
              className={styles.refreshBtn}
              onClick={() => fetchVersions(true)}
              disabled={versionsRefreshing}
              title="Rafraîchir"
            >
              <FiRefreshCcw className={versionsRefreshing ? styles.spinning : ""} />
            </button>
            <Button
              styles="btnPrimary"
              text="Ajouter une version"
              svg={<FiPlus style={{ marginLeft: 6 }} />}
              onPress={openAdd}
            />
          </div>
        </div>

        <div className={styles.moduleCard}>
          {versionsLoading ? (
            <div className={styles.loaderWrapper}><Loader /></div>
          ) : (
            <div className={styles.tableContainer}>
              {/* Header */}
              <div className={`${styles.tableRow} ${styles.tableHeader}`}>
                <span>Version</span>
                <span>Canal</span>
                <span>Date obligatoire</span>
                <span>Créée le</span>
                <span>Modifiée le</span>
                <span>Actions</span>
              </div>

              {/* Rows */}
              {versions.length === 0 ? (
                <div className={styles.emptyRow}>Aucune version enregistrée.</div>
              ) : (
                versions.map((v) => (
                  <div key={v.id} className={styles.tableRow}>
                    <span className={styles.versionCell}>{v.version}</span>
                    <span>
                      <span className={`${styles.channelBadge} ${v.channel === "LTS" ? styles.badgeLts : styles.badgeStandard}`}>
                        {v.channel}
                      </span>
                    </span>
                    <span>{formatDate(v.mandatory_date)}</span>
                    <span>{formatDate(v.created_at)}</span>
                    <span>{formatDate(v.updated_at)}</span>
                    <span className={styles.actions}>
                      <button className={styles.actionBtn} title="Modifier" onClick={() => openEdit(v)}>
                        <FiEdit2 />
                      </button>
                      <button className={`${styles.actionBtn} ${styles.actionBtnDanger}`} title="Supprimer" onClick={() => openDelete(v)}>
                        <FiTrash2 />
                      </button>
                    </span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </section>

      {/* ── Modal : formulaire ajout / modif ──────────────────────── */}
      <Modal
        isShowing={isFormShowing}
        hide={toggleForm}
        title={editingId ? "Modifier une version" : "Ajouter une version"}
      >
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Version *</label>
            <input
              className={styles.formInput}
              type="text"
              maxLength={32}
              placeholder="ex : 1.5.17.32"
              required
              value={formData.version}
              onChange={(e) => setFormData((p) => ({ ...p, version: e.target.value }))}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Canal *</label>
            <select
              className={styles.formSelect}
              value={formData.channel}
              onChange={(e) => setFormData((p) => ({ ...p, channel: e.target.value }))}
            >
              <option value="STANDARD">STANDARD</option>
              <option value="LTS">LTS</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Date obligatoire</label>
            <input
              className={styles.formInput}
              type="date"
              value={formData.mandatory_date}
              onChange={(e) => setFormData((p) => ({ ...p, mandatory_date: e.target.value }))}
            />
            <span className={styles.formHint}>Laisser vide pour NULL</span>
          </div>

          <div className={styles.formActions}>
            <Button styles="btnOutline" text="Annuler" onPress={toggleForm} type="button" disabled={formSaving} />
            <Button styles="btnPrimary" text={formSaving ? "Sauvegarde…" : editingId ? "Mettre à jour" : "Créer"} type="submit" disabled={formSaving} />
          </div>
        </form>
      </Modal>

      {/* ── Modal : confirmation suppression ──────────────────────── */}
      <Modal
        isShowing={isDeleteShowing}
        hide={toggleDelete}
        title="Confirmer la suppression"
      >
        <div className={styles.deleteConfirm}>
          <p>
            Voulez-vous vraiment supprimer la version{" "}
            <strong>{deleteTarget?.version}</strong> du canal{" "}
            <strong>{deleteTarget?.channel}</strong> ?
          </p>
          <div className={styles.formActions}>
            <Button styles="btnOutline" text="Annuler" onPress={toggleDelete} disabled={deleteLoading} />
            <Button styles="btnDanger" text={deleteLoading ? "Suppression…" : "Supprimer"} onPress={handleDelete} disabled={deleteLoading} />
          </div>
        </div>
      </Modal>

      {/* ── Modal : confirmation toggle réglage global ─────────────── */}
      <Modal
        isShowing={isToggleConfirmShowing}
        hide={() => { toggleConfirmModal(); setPendingToggle(null); }}
        title="Confirmer la modification"
      >
        <div className={styles.deleteConfirm}>
          <p>
            {pendingToggle?.field === "update_disabled" && (
              pendingToggle.nextValue
                ? <>Voulez-vous vraiment <strong>désactiver les mises à jour</strong> sur toutes les installations Hearing Space ?</>
                : <>Voulez-vous vraiment <strong>réactiver les mises à jour</strong> sur toutes les installations Hearing Space ?</>
            )}
            {pendingToggle?.field === "calibration_disabled" && (
              pendingToggle.nextValue
                ? <>Voulez-vous vraiment <strong>désactiver les calibrations</strong> sur toutes les installations Hearing Space ?</>
                : <>Voulez-vous vraiment <strong>réactiver les calibrations</strong> sur toutes les installations Hearing Space ?</>
            )}
          </p>
          <div className={styles.formActions}>
            <Button styles="btnOutline" text="Annuler" onPress={() => { toggleConfirmModal(); setPendingToggle(null); }} disabled={settingsSaving} />
            <Button styles="btnPrimary" text="Confirmer" onPress={confirmToggle} disabled={settingsSaving} />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default HsSettings;
